import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import { useLang } from "../../contexts/LangContext";
import { tx } from "../../i18n";
import { useAuth } from "../../contexts/AuthContext";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../../components/ui/accordion";
import {
  ArrowRight, ShieldCheck, Wallet, Languages, Check, Sparkles,
  TrendingUp, MessageCircle, Smartphone, Globe, Target, Coins,
  LineChart, ArrowUpRight, AlertCircle, Receipt, PiggyBank, Bot,
  Mail, BarChart3, CalendarDays, Clock, FileText, HeartHandshake,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import { useSupportInfo } from "../../hooks/useSupportInfo";
import { LumfiMark, LumfiWordmark } from "../../components/LumfiLogo";
import { LandingSEO } from "./LandingSEO";
import { LandingPricing } from "./LandingPricing";
import { LandingHeroAnimated } from "./LandingHeroAnimated";
import { LandingCalculator } from "./LandingCalculator";
import { LandingArchetypes } from "./LandingArchetypes";
import { LandingWhatsAppCTA } from "./LandingWhatsAppCTA";
import { LeadChatWidget } from "../../components/landing/LeadChatWidget";
import { ArchetypeQuiz } from "../../components/landing/ArchetypeQuiz";

const SUPPORTED = [
  { cc: "AR", flag: "🇦🇷", cur: "ARS" },
  { cc: "BR", flag: "🇧🇷", cur: "BRL" },
  { cc: "CL", flag: "🇨🇱", cur: "CLP" },
  { cc: "CO", flag: "🇨🇴", cur: "COP" },
  { cc: "MX", flag: "🇲🇽", cur: "MXN" },
  { cc: "PE", flag: "🇵🇪", cur: "PEN" },
  { cc: "UY", flag: "🇺🇾", cur: "UYU" },
];

// Number that animates from 0 → target on mount (CSS-only, no deps)
function CountUp({ value, prefix = "", duration = 1400, className = "" }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(value * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <span className={className}>{prefix}{n.toLocaleString("es-AR")}</span>;
}

function Eyebrow({ children, accent = "emerald" }) {
  const color = accent === "amber" ? "text-amber-700" : "text-emerald-700";
  return (
    <div className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold ${color}`}>
      <span className="h-px w-6 bg-current opacity-60" />
      {children}
    </div>
  );
}

/**
 * BenefitsBeforeAfter — editorial "antes / con Lumfi" comparison strip.
 *
 * Sits between "How it works" and the Features bento. The goal is to
 * make the *outcome* of using Lumfi unambiguous before the visitor sees
 * the features. People skim — this section converts skimmers into
 * believers by showing the cost of NOT using Lumfi (left column) next
 * to what changes with it (right column).
 *
 * Data-testid: landing-benefits-before-after
 */
function BenefitsBeforeAfter({ lang }) {
  const isEs = lang === "es";
  const isEn = lang === "en";
  const rows = isEs
    ? [
        { before: "Abres 3 apps el lunes para saber tu cash real", after: "Un solo número en la primera pantalla", Icon: BarChart3 },
        { before: "Cobras un cierre y te lo gastas en 2 semanas", after: "Lumfi reparte tu cash en las semanas que viene", Icon: CalendarDays },
        { before: "Te enteras de las sorpresas el día 28", after: "Alerta el día 8 cuando algo no cuadra", Icon: Clock },
        { before: "El contador te pide “el resumen de junio”", after: "Exporte mensual con un click", Icon: FileText },
        { before: "Vives con la sensación de que algo se te escapa", after: "Sabes cuánto puedes gastar esta semana sin culpa", Icon: HeartHandshake },
      ]
    : isEn
    ? [
        { before: "Open 3 apps on Monday just to see your real cash", after: "One number on the first screen", Icon: BarChart3 },
        { before: "Cash a closing and burn it in 2 weeks", after: "Lumfi splits your cash across the weeks ahead", Icon: CalendarDays },
        { before: "Find out about surprises on the 28th", after: "Alerted on the 8th when something is off", Icon: Clock },
        { before: "Accountant asks 'the June summary'", after: "Monthly export in one click", Icon: FileText },
        { before: "Live with a nagging feeling something slips", after: "Know what you can spend this week — guilt-free", Icon: HeartHandshake },
      ]
    : [
        { before: "Abre 3 apps na segunda para saber seu caixa real", after: "Um número na primeira tela", Icon: BarChart3 },
        { before: "Recebe um fechamento e gasta em 2 semanas", after: "Lumfi distribui o caixa pelas semanas", Icon: CalendarDays },
        { before: "Descobre as surpresas no dia 28", after: "Alertado no dia 8 quando algo está fora", Icon: Clock },
        { before: "Contador pede 'o resumo de junho'", after: "Exporte mensal com um clique", Icon: FileText },
        { before: "Sente que algo está te escapando", after: "Sabe quanto pode gastar essa semana sem culpa", Icon: HeartHandshake },
      ];

  return (
    <section
      data-testid="landing-benefits-before-after"
      className="relative bg-cream-200/70 border-y border-cream-300"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-28">
        <div className="text-center max-w-3xl mx-auto">
          <Eyebrow accent="amber">
            {isEs ? "Antes vs. Con Lumfi" : isEn ? "Before vs. With Lumfi" : "Antes vs. Com Lumfi"}
          </Eyebrow>
          <h2 className="font-serif-display mt-4 sm:mt-5 text-3xl sm:text-4xl lg:text-5xl leading-tight font-medium">
            {isEs
              ? "Lo que cambia desde el primer lunes"
              : isEn
              ? "What changes from your first Monday"
              : "O que muda já na primeira segunda"}
          </h2>
          <p className="mt-4 text-slate-600 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto">
            {isEs
              ? "Sin promesas: cinco diferencias concretas que vas a sentir en la primera semana."
              : isEn
              ? "No fluff: five concrete differences you feel in the first week."
              : "Sem promessas: cinco diferenças concretas que você sente já na primeira semana."}
          </p>
        </div>

        <div className="mt-10 sm:mt-14 grid lg:grid-cols-2 gap-3 sm:gap-4">
          {/* Before column header */}
          <div className="hidden lg:block lg:col-span-1 text-[11px] uppercase tracking-[0.22em] font-bold text-amber-700 pl-1">
            {isEs ? "Tu lunes sin Lumfi" : isEn ? "Your Monday without Lumfi" : "Sua segunda sem Lumfi"}
          </div>
          <div className="hidden lg:block lg:col-span-1 text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-700 pl-1">
            {isEs ? "Con Lumfi" : isEn ? "With Lumfi" : "Com Lumfi"}
          </div>

          {rows.map((r, i) => (
            <div key={i} className="contents">
              <div
                className="bg-white border border-amber-200/70 rounded-2xl p-5 sm:p-6 flex items-start gap-4"
                data-testid={`benefit-before-${i}`}
              >
                <span className="h-9 w-9 rounded-xl bg-amber-50 border border-amber-200/70 flex items-center justify-center shrink-0 mt-0.5">
                  <r.Icon className="h-4 w-4 text-amber-700" strokeWidth={1.7} />
                </span>
                <div>
                  <span className="block text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700 lg:hidden mb-1">
                    {isEs ? "Antes" : isEn ? "Before" : "Antes"}
                  </span>
                  <p className="text-sm sm:text-base text-slate-700 leading-relaxed line-through decoration-amber-400/60 decoration-1 underline-offset-2">
                    {r.before}
                  </p>
                </div>
              </div>
              <div
                className="relative bg-slate-900 text-white border border-emerald-500/30 rounded-2xl p-5 sm:p-6 flex items-start gap-4 overflow-hidden"
                data-testid={`benefit-after-${i}`}
              >
                <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-emerald-500/20 blur-2xl" aria-hidden />
                <span className="relative h-9 w-9 rounded-xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="h-4 w-4 text-emerald-300" strokeWidth={2.2} />
                </span>
                <div className="relative">
                  <span className="block text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-300 lg:hidden mb-1">
                    {isEs ? "Con Lumfi" : isEn ? "With Lumfi" : "Com Lumfi"}
                  </span>
                  <p className="text-sm sm:text-base text-slate-100 leading-relaxed font-medium">
                    {r.after}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const { t, lang, setLang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  // Country state shared across landing components — populated by the
  // /geo endpoint in <LandingPricing>; we also seed it from
  // sessionStorage on first paint so the hero/calculator don't flash
  // the default CO before the geo call resolves on revisits.
  const [country, setCountry] = useState(() => {
    try {
      const cached = sessionStorage.getItem("lumfi.landing.geo");
      if (cached) return JSON.parse(cached).country || "CO";
    } catch (_e) { /* sessionStorage unavailable — keep default */ }
    return "CO";
  });

  useEffect(() => {
    if (user && user.onboarded) navigate("/dashboard");
    else if (user) navigate("/onboarding");
  }, [user, navigate]);

  // Listen to storage updates so when <LandingPricing> changes the
  // active country via the dropdown, hero + calc react instantly.
  useEffect(() => {
    const sync = () => {
      try {
        const cached = sessionStorage.getItem("lumfi.landing.geo");
        if (cached) setCountry(JSON.parse(cached).country || "CO");
      } catch (_e) { /* sessionStorage unavailable — ignore */ }
    };
    window.addEventListener("storage", sync);
    // Also poll every 1.5s for in-tab updates (storage event doesn't fire same-tab)
    const poll = setInterval(sync, 1500);
    return () => { window.removeEventListener("storage", sync); clearInterval(poll); };
  }, []);

  return (
    <div className="min-h-screen bg-cream-100 text-slate-900 relative overflow-x-hidden" data-testid="landing-root">
      <LandingSEO lang={lang} />
      {/* ambient editorial halos */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 h-[480px] w-[480px] rounded-full bg-emerald-200/35 blur-3xl landing-ambient" />
        <div className="absolute top-[480px] -left-32 h-[420px] w-[420px] rounded-full bg-amber-200/30 blur-3xl landing-ambient" style={{ animationDelay: "3s" }} />
        <div className="absolute inset-0 grain-overlay opacity-50" />
      </div>

      {/* ----- NAV ----- */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-cream-100/80 border-b border-cream-300/70">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <LumfiWordmark height={32} className="hidden sm:block" />
            <LumfiMark size={36} className="sm:hidden" />
          </div>
          <nav className="hidden md:flex items-center gap-7 text-sm text-slate-600">
            <a href="#quiz" className="hover:text-slate-900 transition-colors">{tx(lang, "Quiz", "¿Cuál es para tú?", "Quiz")}</a>
            <a href="#problem" className="hover:text-slate-900 transition-colors">¿Para qué?</a>
            <a href="#how" className="hover:text-slate-900 transition-colors">Cómo funciona</a>
            <a href="#features" className="hover:text-slate-900 transition-colors">Funciona­lidades</a>
            <a href="#pricing" className="hover:text-slate-900 transition-colors">Precios</a>
            <a href="#faq" className="hover:text-slate-900 transition-colors">FAQ</a>
          </nav>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button /* design-system: ALLOW legacy-todo */
                  data-testid="landing-lang-switcher"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider text-slate-700 border border-slate-200 bg-white/70 hover:bg-white transition"
                >
                  <Languages className="h-3.5 w-3.5" />
                  {lang.toUpperCase()}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuLabel className="text-xs">{t("language") || "Language"}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {[["en", "English"], ["es", "Español"], ["pt", "Português"]].map(([code, label]) => (
                  <DropdownMenuItem key={code} data-testid={`landing-lang-${code}`} onClick={() => setLang(code)} className="cursor-pointer">
                    <span className="flex-1">{label}</span>
                    {lang === code && <Check className="h-4 w-4 text-emerald-600" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Link to="/login">
              <Button data-testid="landing-nav-login-btn" variant="ghost" className="text-slate-700 hover:text-slate-900">{t("sign_in")}</Button>
            </Link>
            <Link to="/register" className="hidden sm:block">
              <Button data-testid="landing-nav-register-btn" className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-5">
                {t("sign_up")}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* ----- HERO ----- */}
      <section className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-10 sm:pt-16 lg:pt-24 pb-12 sm:pb-16 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-10 sm:gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6 fade-up">
            <Eyebrow>{t("landing_eyebrow")}</Eyebrow>
            <h1 className="font-serif-display mt-5 sm:mt-6 text-4xl sm:text-5xl lg:text-[5.5rem] leading-[0.98] sm:leading-[0.95] font-medium text-slate-900">
              <span className="block">{t("landing_hero_title_a")}</span>
              <span className="block italic text-emerald-700">{t("landing_hero_title_b")}</span>
            </h1>
            <p className="mt-5 sm:mt-7 text-base sm:text-lg leading-relaxed text-slate-600 max-w-xl">
              {t("landing_hero_sub")}
            </p>
            <div className="mt-7 sm:mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/register">
                <Button
                  data-testid="landing-hero-primary-cta"
                  size="lg"
                  className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white text-base px-7 py-6 rounded-full gap-2 shadow-[0_8px_30px_-8px_rgba(22,163,74,0.55)] hover:translate-y-[-2px] transition-all"
                >
                  {t("landing_hero_cta")} <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <a href="#how">
                <Button
                  data-testid="landing-hero-secondary-cta"
                  size="lg"
                  variant="ghost"
                  className="w-full sm:w-auto text-base px-6 py-6 text-slate-700 hover:bg-transparent hover:text-slate-900 underline-offset-4 hover:underline"
                >
                  {t("landing_hero_secondary")} <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </a>
            </div>
            <div className="mt-7 sm:mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500">
              <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-600" />{t("landing_hero_chip_1")}</span>
              <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-600" />{t("landing_hero_chip_2")}</span>
              <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-600" />{t("landing_hero_chip_3")}</span>
            </div>
          </div>

          {/* Hero visual — animated cash widget */}
          <div className="lg:col-span-6 fade-up-2">
            <LandingHeroAnimated country={country} lang={lang} />
          </div>
        </div>

        {/* Trust strip */}
        <div className="mt-14 sm:mt-20 lg:mt-28">
          <div className="text-center">
            <Eyebrow accent="amber">{t("landing_trust_label")}</Eyebrow>
          </div>
          <div className="mt-5 sm:mt-6 flex flex-wrap items-center justify-center gap-x-6 sm:gap-x-8 gap-y-3 text-slate-500">
            {SUPPORTED.map((c) => (
              <div key={c.cc} className="flex items-center gap-2 text-sm" data-testid={`landing-supported-${c.cc}`}>
                <span className="text-base leading-none">{c.flag}</span>
                <span className="font-mono-num text-xs tracking-wider text-slate-600">{c.cur}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ----- QUIZ — "¿Cuál Lumfi es para tú?" — sits right after the
          hero so multi-persona visitors can self-route in 30s ----- */}
      <ArchetypeQuiz lang={lang} />

      {/* ----- PROBLEM FRAMING ----- */}
      <section id="problem" className="relative bg-cream-200/70 border-y border-cream-300">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32 grid lg:grid-cols-12 gap-10 sm:gap-12 lg:gap-20">          <div className="lg:col-span-5">
            <Eyebrow accent="amber">{t("landing_problem_eyebrow")}</Eyebrow>
            <h2 className="font-serif-display mt-4 sm:mt-5 text-3xl sm:text-4xl lg:text-5xl leading-[1.05] font-medium">
              {t("landing_problem_h2")}
            </h2>
          </div>
          <div className="lg:col-span-7">
            <ul className="space-y-6 sm:space-y-7 relative border-l border-stone-300/70 pl-6 sm:pl-7">
              {[
                t("landing_problem_q1"),
                t("landing_problem_q2"),
                t("landing_problem_q3"),
              ].map((q, i) => (
                <li key={i} className="relative">
                  <span className="absolute -left-[33px] sm:-left-[37px] top-1 h-4 w-4 rounded-full bg-amber-500/20 border-2 border-amber-600 flex items-center justify-center">
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-600" />
                  </span>
                  <p className="font-serif-display text-xl sm:text-2xl lg:text-[1.65rem] leading-snug text-slate-800 italic">&ldquo;{q}&rdquo;</p>
                </li>
              ))}
            </ul>
            <div className="mt-10 sm:mt-12 pl-6 sm:pl-7 border-l border-emerald-600/70">
              <p className="font-serif-display text-2xl sm:text-3xl lg:text-4xl text-emerald-800 leading-tight">
                {t("landing_problem_h2b")}
              </p>
              <p className="mt-4 text-slate-600 text-base leading-relaxed max-w-xl">
                {t("landing_problem_p")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ----- CALCULATOR (interactive lead-magnet) ----- */}
      <section id="calculator" className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-28">
        <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-10">
          <Eyebrow accent="emerald">
            {tx(lang, "Try it now", "Pregunta Ahora", "Teste agora")}
          </Eyebrow>
          <h2 className="font-serif-display mt-4 sm:mt-5 text-2xl sm:text-3xl lg:text-4xl leading-tight font-medium">
            {lang === "es"
              ? "Prueba tu límite seguro semanal en 10 segundos"
              : lang === "en"
              ? "Test your safe weekly limit in 10 seconds"
              : "Teste seu limite semanal em 10 segundos"}
          </h2>
        </div>
        <div className="max-w-2xl mx-auto">
          <LandingCalculator country={country} lang={lang} />
        </div>
      </section>

      {/* ----- HOW IT WORKS ----- */}
      <section id="how" className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
        <div className="text-center max-w-3xl mx-auto">
          <Eyebrow>{t("landing_steps_eyebrow")}</Eyebrow>
          <h2 className="font-serif-display mt-4 sm:mt-5 text-3xl sm:text-4xl lg:text-5xl leading-tight font-medium">
            {t("landing_steps_h2")}
          </h2>
        </div>
        <div className="mt-10 sm:mt-16 grid md:grid-cols-3 gap-5 sm:gap-8 lg:gap-10">
          {[
            { n: "01", icon: Wallet, t: t("landing_step_1_t"), d: t("landing_step_1_d") },
            { n: "02", icon: LineChart, t: t("landing_step_2_t"), d: t("landing_step_2_d") },
            { n: "03", icon: Bot, t: t("landing_step_3_t"), d: t("landing_step_3_d") },
          ].map((s, i) => (
            <div key={s.n} className="relative bg-white border border-cream-300 rounded-3xl p-6 sm:p-8 lg:p-10 hover:border-emerald-300 hover:shadow-[0_18px_48px_-24px_rgba(15,23,42,0.18)] transition-all duration-300 group" data-testid={`landing-step-${i+1}`} style={{ animationDelay: `${i * 80}ms` }}>
              <span className="absolute top-4 right-6 font-serif-display text-6xl sm:text-7xl text-stone-200/90 select-none leading-none">{s.n}</span>
              <s.icon className="h-7 w-7 text-emerald-700" strokeWidth={1.5} />
              <h3 className="mt-5 sm:mt-6 font-serif-display text-xl sm:text-2xl font-medium leading-tight">{s.t}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ----- BENEFITS DEEP-DIVE — what you actually walk away with ----- */}
      <BenefitsBeforeAfter lang={lang} />

      {/* ----- FEATURES BENTO ----- */}
      <section id="features" className="relative bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 grain-overlay opacity-30" />
        <div className="absolute top-0 left-1/3 h-80 w-80 rounded-full bg-emerald-500/15 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-amber-500/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
          <div className="max-w-3xl">
            <Eyebrow>{t("landing_features_eyebrow")}</Eyebrow>
            <h2 className="font-serif-display mt-4 sm:mt-5 text-3xl sm:text-4xl lg:text-5xl leading-[1.05] font-medium">
              {t("landing_features_h2")}
            </h2>
          </div>

          <div className="mt-10 sm:mt-14 grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-5 lg:gap-6">
            <FeatureCard className="lg:col-span-7 lg:row-span-2" icon={Coins} title={t("landing_feat_cash_t")} desc={t("landing_feat_cash_d")}>
              <FeatureCashVisual />
            </FeatureCard>
            <FeatureCard className="lg:col-span-5" icon={AlertCircle} title={t("landing_feat_risk_t")} desc={t("landing_feat_risk_d")} accent="amber">
              <div className="mt-5 flex items-center gap-2">
                {[
                  { c: "bg-emerald-500/90", l: "Low" },
                  { c: "bg-amber-400/90", l: "Mid", active: true },
                  { c: "bg-red-500/80", l: "High" },
                ].map((r) => (
                  <div key={r.l} className={`flex-1 py-2 rounded-lg text-center text-[11px] font-semibold tracking-widest uppercase ${r.c} ${r.active ? "ring-2 ring-white/80" : "opacity-50"}`}>
                    {r.l}
                  </div>
                ))}
              </div>
            </FeatureCard>
            <FeatureCard className="lg:col-span-5" icon={MessageCircle} title={t("landing_feat_ai_t")} desc={t("landing_feat_ai_d")}>
              <FeatureChatVisual />
            </FeatureCard>
            <FeatureCard className="lg:col-span-4" icon={Receipt} title={t("landing_feat_rec_t")} desc={t("landing_feat_rec_d")} />
            <FeatureCard className="lg:col-span-4" icon={PiggyBank} title={t("landing_feat_wealth_t")} desc={t("landing_feat_wealth_d")} />
            <FeatureCard className="lg:col-span-4" icon={Globe} title={t("landing_feat_fx_t")} desc={t("landing_feat_fx_d")} accent="amber" />
          </div>
        </div>
      </section>

      {/* ----- BOT INTEGRATIONS MARQUEE ----- */}
      <section className="bg-cream-200/80 border-y border-cream-300 py-14 sm:py-20 lg:py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <Eyebrow>{t("landing_bots_eyebrow")}</Eyebrow>
          <h2 className="font-serif-display mt-4 sm:mt-5 text-3xl sm:text-4xl lg:text-5xl leading-tight font-medium">
            {t("landing_bots_h2")}
          </h2>
          <p className="mt-4 sm:mt-5 text-sm sm:text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
            {t("landing_bots_p")}
          </p>
        </div>
        <div className="mt-12 marquee-mask">
          <div className="marquee-track gap-12 items-center">
            {[...Array(2)].map((_, dup) => (
              <div key={dup} className="flex gap-12 items-center pr-12">
                {[
                  { I: MessageCircle, l: "Telegram" },
                  { I: Smartphone, l: "WhatsApp" },
                  { I: Globe, l: "Web" },
                  { I: Sparkles, l: "AI Copilot" },
                  { I: Receipt, l: "Receivables" },
                  { I: Coins, l: "Cash Flow" },
                  { I: Target, l: "Goals" },
                  { I: LineChart, l: "Forecast" },
                ].map((item, i) => (
                  <div key={`${dup}-${i}`} className="flex items-center gap-3 whitespace-nowrap">
                    <item.I className="h-6 w-6 text-emerald-700" strokeWidth={1.5} />
                    <span className="font-serif-display text-3xl text-slate-700 italic">{item.l}</span>
                    <span className="text-stone-400 text-2xl">·</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ----- ARCHETYPES (who is Lumfi for) ----- */}
      <LandingArchetypes lang={lang} />

      {/* ----- PRICING TEASER ----- */}
      <section id="pricing" className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
        <div className="text-center max-w-3xl mx-auto">
          <Eyebrow>{t("landing_pricing_eyebrow")}</Eyebrow>
          <h2 className="font-serif-display mt-4 sm:mt-5 text-3xl sm:text-4xl lg:text-5xl leading-tight font-medium">
            {t("landing_pricing_h2")}
          </h2>
        </div>
        <div className="mt-10 sm:mt-14">
          <LandingPricing lang={lang} />
        </div>
      </section>

      {/* ----- FAQ ----- */}
      <section id="faq" className="bg-cream-200/70 border-y border-cream-300">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
          <div className="text-center">
            <Eyebrow>{t("landing_faq_eyebrow")}</Eyebrow>
            <h2 className="font-serif-display mt-4 sm:mt-5 text-3xl sm:text-4xl lg:text-5xl leading-tight font-medium">
              {t("landing_faq_h2")}
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-8 sm:mt-12">
            {[1, 2, 3, 4, 5].map((i) => (
              <AccordionItem key={i} value={`q${i}`} className="border-b border-stone-300/60" data-testid={`landing-faq-item-${i}`}>
                <AccordionTrigger className="text-left text-base sm:text-lg font-serif-display font-medium py-5 sm:py-6 hover:no-underline">
                  {t(`landing_faq_q${i}`)}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 text-sm sm:text-base leading-relaxed pb-6 pr-2 sm:pr-6">
                  {t(`landing_faq_a${i}`)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ----- FINAL CTA ----- */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
        <div className="relative rounded-[2rem] lg:rounded-[3rem] bg-slate-900 text-white overflow-hidden p-8 sm:p-12 lg:p-20">
          <div className="absolute inset-0 grain-overlay opacity-30" />
          <div className="absolute -top-40 -right-40 h-[420px] w-[420px] rounded-full bg-emerald-500/25 blur-3xl" />
          <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-amber-400/15 blur-3xl" />
          <div className="relative max-w-2xl mx-auto text-center">
            <Eyebrow>{t("landing_final_eyebrow")}</Eyebrow>
            <h2 className="font-serif-display mt-5 sm:mt-6 text-3xl sm:text-5xl lg:text-6xl leading-[1.05] font-medium">
              {t("landing_final_h2")}
            </h2>
            <p className="mt-5 sm:mt-6 text-slate-300 leading-relaxed text-base sm:text-lg">
              {t("landing_final_p")}
            </p>
            <Link to="/register" className="inline-block mt-8 sm:mt-10">
              <Button
                data-testid="landing-final-cta-btn"
                size="lg"
                className="bg-emerald-500 hover:bg-emerald-400 text-white text-base px-9 py-7 rounded-full gap-2 shadow-[0_18px_48px_-12px_rgba(16,185,129,0.55)] hover:translate-y-[-2px] transition-all"
              >
                {t("landing_final_cta")} <ArrowUpRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ----- FOOTER ----- */}
      <footer className="border-t border-cream-300 bg-cream-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-2.5">
              <LumfiMark size={32} />
              <span className="font-display text-lg font-bold">Lumfi</span>
            </div>
            <p className="mt-4 text-sm text-slate-500 leading-relaxed max-w-xs">
              {t("landing_footer_tag")}
            </p>
            <ContactFooterLink />
          </div>
          <FooterCol label={t("landing_footer_product")} links={[
            { to: "#features", label: t("landing_footer_features") },
            { to: "#pricing", label: t("landing_footer_pricing") },
          ]} />
          <FooterCol label={t("landing_footer_company")} links={[
            { to: "#problem", label: t("landing_footer_about") },
          ]} />
          <FooterCol label={t("landing_footer_legal")} links={[
            { to: "/privacy", label: t("landing_footer_privacy") },
            { to: "/terms", label: t("landing_footer_terms") },
          ]} />
        </div>
        <div className="border-t border-cream-300">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
            <span>© {new Date().getFullYear()} Lumfi. {t("landing_footer_rights")}.</span>
            <div className="flex items-center gap-2 text-slate-500">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
              <span>Private by design</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp CTA — sticky entry to admin chat inbox */}
      <LandingWhatsAppCTA lang={lang} country={country} intent="contact" />

      {/* Floating Lead Chat — bot-first conversation for unauthenticated visitors */}
      <LeadChatWidget lang={lang} country={country} />

      {/* iter-169zs · Sticky mobile bottom CTA — appears once the user
          scrolls past the hero. Boosts conversion by keeping the
          primary action one tap away on small screens. */}
      <MobileStickyCTA t={t} />
    </div>
  );
}

function MobileStickyCTA({ t }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onScroll = () => {
      // Appear after ~600px of scroll (i.e., once the hero is out of
      // the way) and hide near the page bottom so it doesn't double
      // up with the in-page final CTA.
      const y = window.scrollY;
      const fromBottom = document.documentElement.scrollHeight - (y + window.innerHeight);
      setShow(y > 600 && fromBottom > 400);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div
      data-testid="landing-mobile-sticky-cta"
      className={`fixed bottom-0 inset-x-0 z-30 sm:hidden lumfi-thumb-zone transition-transform duration-300 ${
        show ? "translate-y-0" : "translate-y-full"
      }`}
      aria-hidden={!show}
    >
      <div className="mx-3 mb-3 rounded-2xl bg-white border border-cream-300 shadow-[0_20px_44px_-12px_rgba(15,23,42,0.25)] backdrop-blur-xl px-3 py-2.5 flex items-center gap-2.5">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-wider text-emerald-700 font-bold">
            Lumfi Cash Pilot
          </p>
          <p className="text-xs text-slate-700 font-medium truncate">
            {t("landing_hero_chip_2")}
          </p>
        </div>
        <Link to="/register" className="shrink-0">
          <Button
            data-testid="landing-mobile-sticky-cta-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-4 py-2.5 rounded-full gap-1.5 h-auto"
          >
            {t("landing_hero_cta")}
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </Link>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Sub-components                                                     */
/* ------------------------------------------------------------------ */

function HeroSnapshot({ t }) {
  return (
    <div className="relative">
      <div className="relative bg-white border border-cream-300 rounded-[1.75rem] p-7 lg:p-9 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.18)]" data-testid="landing-hero-snapshot">
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Snapshot · Live</span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-[10px] font-bold uppercase tracking-wider border border-amber-200">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
            {t("landing_snap_risk")}
          </span>
        </div>

        <div className="mt-6">
          <div className="text-xs text-slate-500">{t("landing_snap_label")}</div>
          <CountUp value={2300000} prefix="$" duration={1600} className="block font-mono-num text-5xl lg:text-6xl font-medium text-slate-900 mt-1" />
          <div className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-700 font-semibold">
            <TrendingUp className="h-3.5 w-3.5" />+ 12% vs last week
          </div>
        </div>

        <div className="mt-7 p-5 rounded-2xl bg-emerald-50/80 border border-emerald-200/60">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">{t("landing_snap_safe")}</div>
            <Sparkles className="h-3.5 w-3.5 text-emerald-600" />
          </div>
          <CountUp value={700000} prefix="$" duration={1800} className="block font-mono-num text-4xl font-medium text-emerald-700 mt-1 glow-emerald" />
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <div className="p-4 rounded-xl bg-slate-50/80 border border-slate-200/70">
            <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500">{t("landing_snap_pending")}</div>
            <div className="font-mono-num text-lg font-medium text-slate-800 mt-1">$1,150,000</div>
          </div>
          <div className="p-4 rounded-xl bg-slate-50/80 border border-slate-200/70">
            <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500">Next bill</div>
            <div className="font-mono-num text-lg font-medium text-slate-800 mt-1">Mar 15</div>
          </div>
        </div>

        <div className="mt-5 flex items-start gap-2 text-xs text-slate-500">
          <Sparkles className="h-3.5 w-3.5 mt-0.5 text-emerald-600 shrink-0" />
          <span>{t("landing_snap_explain")}</span>
        </div>
      </div>

      {/* floating chat bubble */}
      <div className="hidden lg:block absolute -bottom-6 -left-6 w-[260px] bg-white border border-cream-300 rounded-2xl p-4 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.2)] rotate-[-2deg]">
        <div className="flex items-center gap-2 mb-2">
          <div className="h-7 w-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <Bot className="h-4 w-4" />
          </div>
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Lumfi · Telegram</span>
        </div>
        <p className="text-sm text-slate-700 leading-snug italic">&ldquo;Pagué Netflix 39 pesos&rdquo;</p>
        <p className="mt-2 text-xs text-emerald-700 font-medium leading-snug">→ Registered. New safe-to-use: $700K</p>
      </div>
    </div>
  );
}

function FeatureCard({ className = "", icon: Icon, title, desc, accent = "emerald", children }) {
  const ringColor = accent === "amber" ? "from-amber-400/40" : "from-emerald-400/40";
  return (
    <div className={`relative rounded-3xl bg-white/[0.03] border border-white/10 p-8 backdrop-blur-sm overflow-hidden group hover:bg-white/[0.06] transition-all ${className}`}>
      <div className={`absolute -top-px -left-px h-px w-24 bg-gradient-to-r ${ringColor} to-transparent`} />
      <Icon className="h-7 w-7 text-emerald-300" strokeWidth={1.5} />
      <h3 className="mt-6 font-serif-display text-2xl lg:text-[1.65rem] font-medium leading-tight">{title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-slate-300/80 max-w-md">{desc}</p>
      {children}
    </div>
  );
}

function FeatureCashVisual() {
  return (
    <div className="mt-8 relative">
      <div className="flex items-baseline gap-3">
        <span className="font-mono-num text-5xl lg:text-6xl font-medium text-white">$2,300K</span>
        <span className="text-xs uppercase tracking-widest text-slate-400">real cash</span>
      </div>
      <div className="mt-6 h-2.5 bg-white/10 rounded-full overflow-hidden flex">
        <div className="h-full bg-emerald-400 rounded-l-full" style={{ width: "30%" }} />
        <div className="h-full bg-amber-400" style={{ width: "20%" }} />
        <div className="h-full bg-slate-500/60" style={{ width: "50%" }} />
      </div>
      <div className="mt-3 grid grid-cols-3 gap-3 text-[10px] uppercase tracking-widest text-slate-400">
        <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-400" />Safe</div>
        <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-400" />Reserve</div>
        <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-slate-500" />Committed</div>
      </div>
    </div>
  );
}

function FeatureChatVisual() {
  return (
    <div className="mt-6 space-y-2">
      <div className="bg-white/8 rounded-2xl rounded-bl-md p-3 max-w-[80%] text-sm text-slate-100/90 italic" style={{ background: "rgba(255,255,255,0.08)" }}>
        &ldquo;Cobré 5.000 USD del cliente X&rdquo;
      </div>
      <div className="ml-auto bg-emerald-500/20 border border-emerald-400/40 rounded-2xl rounded-br-md p-3 max-w-[85%] text-sm text-emerald-100">
        ✓ Registrado. Tu efectivo subió a $7.3M ARS.
      </div>
    </div>
  );
}

function FooterCol({ label, links }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">{label}</div>
      <ul className="mt-4 space-y-2.5">
        {links.map((l) => (
          <li key={l.to}>
            <a href={l.to} className="text-sm text-slate-700 hover:text-slate-900 transition-colors">{l.label}</a>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Public contact email surfaced under the brand block. Hidden if the
 *  admin hasn't configured `support_contact_email` in /admin → Soporte. */
function ContactFooterLink() {
  const { data } = useSupportInfo();
  const email = data?.contact_email;
  const { lang } = useLang();
  if (!email) return null;
  return (
    <a
      href={`mailto:${email}`}
      data-testid="landing-footer-contact"
      className="mt-5 inline-flex items-center gap-2 text-sm text-slate-700 hover:text-emerald-700 transition-colors group"
    >
      <Mail className="h-3.5 w-3.5 text-slate-400 group-hover:text-emerald-600 transition-colors" />
      <span className="border-b border-slate-300 group-hover:border-emerald-500 transition-colors">
        {lang === "en" ? "Contact us" : lang === "pt" ? "Fale conosco" : "Cuentactanos"}
      </span>
      <span className="text-xs text-slate-400 group-hover:text-emerald-600">→ {email}</span>
    </a>
  );
}
